import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import * as Icons from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
  DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import CanonicalUploader from "@/components/documents/CanonicalUploader";

/**
 * StaffUploadDialog — richer general-purpose upload for staff on the Client Workspace.
 *
 * Reuses the canonical document pipeline (POST /api/documents-intel/upload) exactly
 * as LockedUploadDialog does, but adds metadata fields: category (required), tax year,
 * jurisdiction, and an optional note stored as document_purpose.
 *
 * Key invariants:
 *  - upload_source is always "staff_upload" — the backend sets source/uploaded_by.
 *  - No request_id is sent here — this is the general upload workflow.
 *    Requirement-specific uploads use LockedUploadDialog with requestId.
 *  - reference_material / advisory_research categories are guarded server-side
 *    against auto-satisfying open document requests.
 *  - Category is required before files can be dropped/picked.
 */

// Categories placed at the top of the grouped dropdown for the most common
// staff-upload use cases. Presentation only — same canonical keys as elsewhere.
const STAFF_FIRST_KEYS = [
  "deliverables",           // STR deliverables, Executive Summaries, IRS Filing Confirmations
  "reference_material",
  "advisory_research",
  "supporting_documentation",
  "other",
];

export function StaffUploadDialog({
  open,
  onClose,
  onDone,
  clientId,
  clientName,
  engagementId,
}) {
  const [cats, setCats] = useState([]);
  const [category, setCategory] = useState("");
  const [taxYear, setTaxYear] = useState("");
  const [jurisdiction, setJurisdiction] = useState("");
  const [note, setNote] = useState("");
  const navigate = useNavigate();

  // Reload categories and reset form each time dialog opens
  useEffect(() => {
    if (!open) return;
    api
      .get("/documents-intel/categories")
      .then((r) => setCats(r.data.categories || []))
      .catch(() => {});
    setCategory("");
    setTaxYear("");
    setJurisdiction("");
    setNote("");
  }, [open]);

  // Grouped presentation: staff-upload group first (sorted by STAFF_FIRST_KEYS order),
  // then the remaining canonical categories in their existing order.
  const staffGroupCats = STAFF_FIRST_KEYS
    .map((k) => cats.find((c) => c.key === k))
    .filter(Boolean);
  const otherGroupCats = cats.filter((c) => !STAFF_FIRST_KEYS.includes(c.key));

  // uploadFn captures metadata from form state at time of upload.
  const uploadFn = useCallback(
    (file, { signal, asVersion, separate, note: versionNote, onProgress }) => {
      const form = new FormData();
      form.append("file", file);
      form.append("destination_type", "client");
      form.append("destination_id", clientId);
      if (engagementId) form.append("engagement_id", engagementId);
      form.append("category", category);
      // upload_source is set by the backend from the token; passing it in form
      // data allows it to appear in document metadata immediately.
      form.append("upload_source", "staff_upload");
      if (taxYear.trim()) form.append("tax_year", taxYear.trim());
      if (jurisdiction.trim()) form.append("jurisdiction", jurisdiction.trim());
      if (note.trim()) form.append("document_purpose", note.trim());
      if (versionNote) form.append("version_note", versionNote);
      if (asVersion) form.append("as_version", "true");
      if (separate) form.append("separate", "true");

      return api
        .post("/documents-intel/upload", form, {
          headers: { "Content-Type": "multipart/form-data" },
          signal,
          onUploadProgress: (e) => {
            if (e.total) onProgress(Math.round((e.loaded / e.total) * 100));
          },
        })
        .then((r) => r.data)
        .catch((err) => {
          const detail = err.response?.data?.detail;
          if (err.response?.status === 409 && detail?.code === "duplicate_document") {
            const e = new Error(
              "This file was already uploaded — keep as a new version?"
            );
            e.duplicate = detail.existing;
            throw e;
          }
          throw new Error(formatApiErrorDetail(detail) || "Upload failed.");
        });
    },
    [category, taxYear, jurisdiction, note, clientId, engagementId]
  );

  const pollStatus = useCallback(
    (id) => api.get(`/documents-intel/${id}/status`).then((r) => r.data),
    []
  );

  return (
    <Dialog
      open={!!open}
      onOpenChange={(v) => {
        if (!v) onClose();
      }}
    >
      <DialogContent className="max-w-lg" data-testid="staff-upload-dialog">
        <DialogHeader>
          <DialogTitle>Upload Document</DialogTitle>
          <DialogDescription className="sr-only">
            Upload a document to this client's workspace. Select a category and
            optionally provide metadata before dropping or choosing your file.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Destination + provenance lock */}
          <div
            className="flex items-center gap-2 rounded-lg border border-blue-200 bg-blue-50/60 px-3 py-2 text-sm text-blue-800"
            data-testid="staff-upload-destination"
          >
            <Icons.Lock className="h-3.5 w-3.5 shrink-0" />
            <span className="font-medium truncate">
              Uploading to {clientName || "this client"}
            </span>
            <span className="ml-auto shrink-0 text-[11px] bg-blue-100 text-blue-700 px-2 py-0.5 rounded font-semibold tracking-wide">
              Staff Upload
            </span>
          </div>

          {/* Category — required; uploader is disabled until selected */}
          <div>
            <label className="text-sm font-semibold text-slate-700 block mb-1.5">
              Category{" "}
              <span className="text-red-500" aria-hidden>*</span>
              <span className="sr-only">(required)</span>
            </label>
            <select
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              className="w-full text-sm border border-slate-200 rounded-lg px-3 py-2 bg-white text-slate-800 focus:outline-none focus:ring-2 focus:ring-blue-400"
              data-testid="staff-upload-category"
            >
              <option value="">— Select a category —</option>

              {staffGroupCats.length > 0 && (
                <optgroup label="Staff Uploads">
                  {staffGroupCats.map((c) => (
                    <option key={c.key} value={c.key}>
                      {c.label}
                    </option>
                  ))}
                </optgroup>
              )}

              {otherGroupCats.length > 0 && (
                <optgroup label="Document Categories">
                  {otherGroupCats.map((c) => (
                    <option key={c.key} value={c.key}>
                      {c.label}
                    </option>
                  ))}
                </optgroup>
              )}
            </select>

            {!category && (
              <p className="text-xs text-slate-400 mt-1">
                For an externally prepared draft or reference material, choose
                <em> Reference Material</em> or <em>Advisory / Research</em>.
              </p>
            )}
          </div>

          {/* Tax Year + Jurisdiction on one row */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-sm font-semibold text-slate-700 block mb-1.5">
                Tax Year{" "}
                <span className="text-slate-400 font-normal text-xs">(optional)</span>
              </label>
              <input
                type="text"
                placeholder="e.g. 2024"
                value={taxYear}
                onChange={(e) => setTaxYear(e.target.value)}
                className="w-full text-sm border border-slate-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-400"
                data-testid="staff-upload-tax-year"
              />
            </div>
            <div>
              <label className="text-sm font-semibold text-slate-700 block mb-1.5">
                Jurisdiction{" "}
                <span className="text-slate-400 font-normal text-xs">(optional)</span>
              </label>
              <input
                type="text"
                placeholder="e.g. US, Thailand"
                value={jurisdiction}
                onChange={(e) => setJurisdiction(e.target.value)}
                className="w-full text-sm border border-slate-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-400"
                data-testid="staff-upload-jurisdiction"
              />
            </div>
          </div>

          {/* Note / description */}
          <div>
            <label className="text-sm font-semibold text-slate-700 block mb-1.5">
              Note{" "}
              <span className="text-slate-400 font-normal text-xs">(optional)</span>
            </label>
            <textarea
              rows={2}
              placeholder="e.g. External AI-generated STR draft — uploaded for reference"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              className="w-full text-sm border border-slate-200 rounded-lg px-3 py-2 resize-none focus:outline-none focus:ring-2 focus:ring-blue-400"
              data-testid="staff-upload-note"
            />
            <p className="text-xs text-slate-400 mt-1">
              Stored as document purpose — visible in provenance and document history.
            </p>
          </div>

          {/* Canonical file uploader — disabled until category is chosen */}
          <CanonicalUploader
            uploadFn={uploadFn}
            onFileUploaded={() => {}}
            onAllComplete={() => onDone && onDone()}
            pollStatus={pollStatus}
            onView={(id) => navigate(`/documents/${id}`)}
            disabled={!category}
            disabledHint="Select a category above to enable file upload."
            testId="staff-uploader"
          />
        </div>

        <DialogFooter>
          <button
            onClick={onClose}
            className="text-sm px-4 py-2 rounded-lg bg-slate-900 text-white font-medium hover:bg-slate-700 transition-colors"
            data-testid="staff-upload-close"
          >
            Done
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default StaffUploadDialog;
